import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { type DateRange } from 'react-day-picker';

import { type SourcingMetricsFilterRange } from 'api-clients/monolith';

import { HiringGoalModal } from '@/components/pages/channels/hiring-goals-modal';
import {
  LIFETIME,
  type DateRangePreset,
} from '@/components/ui/date-range-picker';
import { HireStats } from '@/components/ui/hire-stats';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useMetricsFilterParams } from '@/hooks/use-metrics-filter-params';
import { useOpeningSlug } from '@/hooks/use-opening-slug';

import { useIsFetching } from '@tanstack/react-query';

import { queryKeys } from '@/query-keys';

import { DateRangePanel } from './date-range-panel';
import { DateRangeView } from './date-range-view';
import {
  applyMetricsFilterToUrl,
  getBackendMetricsDateRangeState,
  getMetricsFilterApply,
  resolveMetricsDisplayState,
} from './date-range-view/filter-utils';
import { HiringGoalView } from './hiring-goal-view';
import { type MetricsViewMode, MetricsViewToggle } from './metrics-view-toggle';

export const OPENING_METRICS_PANEL_ID = 'opening-metrics-panel';

function Divider() {
  return <div className="bg-fx-neutral-200 w-px self-stretch" aria-hidden />;
}

export function HiringGoal() {
  const openingSlug = useOpeningSlug();
  const {
    opening,
    hiringGoal,
    analyticsData,
    hiringGoalEditUrl,
    filterDuration,
  } = useOpeningDetails();
  const {
    startDate,
    endDate,
    filterRange,
    metricsViewMode,
    setMetricsViewMode,
  } = useOpeningContext();
  const { applyFilterRange, setMetricsFilter } = useMetricsFilterParams();

  const dateRangeTabDefaults = useMemo(
    () => getBackendMetricsDateRangeState(opening.defaultMetricsPeriod, null),
    [opening.defaultMetricsPeriod],
  );

  const [isHiringGoalModalOpen, setIsHiringGoalModalOpen] = useState(false);

  const hasUrlMetricsFilter =
    Boolean(startDate && endDate) || Boolean(filterRange);

  const metricsDisplayState = useMemo(
    () =>
      resolveMetricsDisplayState({
        startDate,
        endDate,
        filterRange,
        defaultMetricsPeriod: opening.defaultMetricsPeriod,
        filterDuration,
      }),
    [
      startDate,
      endDate,
      filterRange,
      opening.defaultMetricsPeriod,
      filterDuration,
    ],
  );

  const handleViewModeChange = useCallback(
    (mode: MetricsViewMode) => {
      if (mode === 'date_range') {
        const switchingFromHiringGoal = metricsViewMode === 'hiring_goal';
        if (switchingFromHiringGoal) {
          const { preset, urlApply } = dateRangeTabDefaults;
          const defaultPeriod: SourcingMetricsFilterRange | null =
            preset === LIFETIME
              ? LIFETIME
              : urlApply?.type === 'filter_range'
                ? urlApply.filterRange
                : null;
          setMetricsViewMode('date_range', defaultPeriod);
        } else {
          // Already in date_range — preserve the user's manual URL filter.
          setMetricsViewMode('date_range');
        }
      } else {
        setMetricsViewMode('hiring_goal');
      }
    },
    [metricsViewMode, dateRangeTabDefaults, setMetricsViewMode],
  );

  useLayoutEffect(() => {
    if (hasUrlMetricsFilter) {
      setMetricsViewMode('date_range');
    }
  }, [hasUrlMetricsFilter, setMetricsViewMode]);

  useEffect(() => {
    document
      .getElementById(OPENING_METRICS_PANEL_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [setMetricsViewMode]);

  const handleMetricsRangeApply = useCallback(
    ({ preset, range }: { preset?: DateRangePreset; range: DateRange }) => {
      const apply = getMetricsFilterApply(preset, range);
      if (!apply) return;

      applyMetricsFilterToUrl(apply, { applyFilterRange, setMetricsFilter });
    },
    [applyFilterRange, setMetricsFilter],
  );

  const isHiringGoalFetching = Boolean(
    useIsFetching({ queryKey: queryKeys.openingDetails.detail }),
  );

  return (
    <div
      id={OPENING_METRICS_PANEL_ID}
      className="flex w-full max-w-[570px] flex-col gap-4 pb-4"
    >
      <MetricsViewToggle
        value={metricsViewMode}
        onChange={handleViewModeChange}
      />

      {isHiringGoalFetching && <HiringGoalContentSkeleton />}

      {!isHiringGoalFetching &&
        (metricsViewMode === 'date_range' ? (
          <DateRangeView
            appliedDate={metricsDisplayState.range}
            appliedPreset={metricsDisplayState.preset}
            onApply={handleMetricsRangeApply}
            hireStats={analyticsData.hire_stats}
            openingSlug={openingSlug}
          />
        ) : (
          <HiringGoalView
            hiringGoal={hiringGoal}
            hiringGoalEditUrl={hiringGoalEditUrl}
            onViewPastGoals={() => setIsHiringGoalModalOpen(true)}
          />
        ))}

      <HiringGoalModal
        open={isHiringGoalModalOpen}
        onOpenChange={setIsHiringGoalModalOpen}
      />
    </div>
  );
}

function HiringGoalContentSkeleton() {
  return (
    <div className="flex items-start gap-6">
      <DateRangePanel.Skeleton />
      <Divider />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Skeleton className="h-[12px] w-[32px]" />
        <HireStats.Skeleton />
      </div>
    </div>
  );
}

HiringGoal.Skeleton = function HiringGoalSkeleton() {
  return (
    <div className="flex w-full max-w-[538px] flex-col gap-4 pb-4">
      <Skeleton className="h-[38px] w-full rounded-[10px]" />
      <HiringGoalContentSkeleton />
    </div>
  );
};
